type WindowStorage = 'localStorage' | 'sessionStorage'

/** 
 * Checks to see if the given storage is available in the current browser.
 * 
 * @param localStorage - Stores data with no expiration date.
 * @param sessionStorage - Is destroyed once the user closes the browser.
 */
export const storageAvailable = (type: WindowStorage): boolean | undefined => {
    var storage
    try {
        storage = window[type]
        var x = '__storage_test__'
        storage.setItem(x, x)
        storage.removeItem(x)
        return true
    } catch (error) {
        return error instanceof DOMException && (
            // everything except Firefox
            error.code === 22 ||
            // Firefox
            error.code === 1014 ||
            // test name field too, because code might not be present
            // everything except Firefox
            error.name === 'QuotaExceededError' ||
            // Firefox
            error.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            (storage && storage.length !== 0)
    }
}

/**
 * Retrieves the item from the given storage and returns its value as a string.
 * 
 * @param type - Accepts either `localStorage` or `sessionStorage`.
 * @param key - A string used to reference a value. Ideally all keys should be saved to the `constants.ts` file under the `_constants` directory.
 * @returns - The value of the key that was saved in storage as a `string`. If the value does not exist, `undefined` is returned.
 */
export const getStorageItem = (type: WindowStorage, key: string) => {
    if (!storageAvailable(type)) return undefined
    return type === 'localStorage' ? localStorage.getItem(key) : sessionStorage.getItem(key)
}

/**
 * If the given storage is available, writes the value at the keys location. Overwrites previous values, if any, at the keys location.
 * 
 * @param type - Accepts either `localStorage` or `sessionStorage`. 
 * @param key - A string used to reference a value. Ideally all keys should be saved to the `constants.ts` file under the `_constants` directory.
 * @param value - Must always be a string to write to a store. The contents of the string can be anything including objects and arrays.
 * @returns
 */
export const setStorageItem = (type: WindowStorage, key: string, value: string) => {
    if (!storageAvailable(type)) return
    else (type === 'localStorage' ? localStorage : sessionStorage).setItem(key, value)
}

/**
 * Destroys the key/s in the given storage including their associated values.
 * 
 * @param type - Accepts either `localStorage` or `sessionStorage`.
 * @param keys - A set of strings used to reference their associated values. Ideally all keys should be saved to the `constants.ts` file under the `_constants` directory.
 * @returns 
 */
export const removeStorageItems = (type: WindowStorage, keys: string | string[]) => {
    if (!storageAvailable(type)) return
    var storage = (type === 'localStorage' ? localStorage : sessionStorage)
    if (typeof keys === 'string') storage.removeItem(keys)
    else if (Array.isArray(keys)) keys.forEach((key) => { storage.removeItem(key) })
}